import React from 'react';
import PropTypes from 'prop-types';
import SafeRegexp from 'escape-string-regexp';
import classnames from 'classnames';

import Helper from '@app/helper';
import { validationIndentifiers } from '@component/form';
import Scrollable from '@component/scrollable';
import TextField from '@component/form/element/textfield';
import I18n from '@module/i18n';

import Icon from '@component/icon';

import { OSLO_GRAY } from '@constant/colors';

const MAX_VISIBLE_ITEMS = 4; // Max items that should fit the visible part of the dropdown
const MAX_TOTAL_ITEMS = 15; // Max items in the complete dropdown
const ROW_HEIGHT = 36;

export default class AutoComplete extends React.Component {
  static propTypes = {
    checked: PropTypes.bool,
    className: PropTypes.string,
    clearable: PropTypes.bool,
    data: PropTypes.array.isRequired,
    defaultValue: PropTypes.string,
    description: PropTypes.string,
    icon: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    showEmptyStateMessage: PropTypes.bool,
    styles: PropTypes.object,
    valid: PropTypes.oneOf([
      validationIndentifiers.NOT_VALID,
      validationIndentifiers.DONT_CHECK,
      validationIndentifiers.VALID,
    ]),
    value: PropTypes.string,
    variant: PropTypes.string,
  };

  static defaultProps = {
    valid: validationIndentifiers.DONT_CHECK,
    variant: 'default',
    showEmptyStateMessage: true,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.state = {
      filteredData: [],
      hideList: true,
      listHeight: false,
      selectedIndex: 0,
      value: this.props.defaultValue,
    };

    this.handleBlur = this.handleBlur.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onReset = this.onReset.bind(this);
  }

  componentDidMount() {
    const { defaultValue } = this.props;
    this.setState({
      filteredData: this.filterList(defaultValue),
    });
    if (defaultValue) {
      this.props.onChange(defaultValue);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        value: this.props.value,
      });
    }
  }

  handleBlur() {
    this.setState({ hideList: true });
  }

  handleListClick(value) {
    this.setState({ value: value, hideList: true });
    this.autoCompleteTextfield.setState({ value: value });
    this.props.onChange(value);
  }

  handleChange(e) {
    this.setState({
      filteredData: this.filterAndSortList(e.target.value),
      value: e.target.value,
      hideList: false,
    });
    this.props.onChange(e.target.value);
  }

  filterAndSortList(value) {
    const filteredList = this.filterList(value);
    const sortedList = this.sortList(filteredList, value);

    return sortedList.slice(0, MAX_TOTAL_ITEMS);
  }

  // Filters the dataset on label and alternative spellings
  filterList(value) {
    const re = new RegExp(SafeRegexp(value || ''), 'i');
    let list = this.props.data.filter(
      (item) => re.test(item.label) || re.test(item.spellings)
    );

    list = list.length
      ? list
      : [{ id: false, label: I18n.t('form.account.error.no_results') }];

    if (list.length < this.state.selectedIndex) {
      this.setState({ selectedIndex: 0 });
    }

    if (this.autocompleteNode && this.autocompleteNode.firstChild) {
      const items =
        list.length >= MAX_VISIBLE_ITEMS ? MAX_VISIBLE_ITEMS : list.length;

      //We need to come up with the listHeight. We calculate that using the height of the existing childs
      this.setState({
        listHeight:
          (this.autocompleteNode.firstChild.offsetHeight || ROW_HEIGHT) * items,
      });
    } else {
      this.setState({ listHeight: false });
    }

    return list;
  }

  // Sorts list based on label and priority
  sortList(original_list, value) {
    let list = original_list.sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });

    // Filter on priority when value has more than 2 characters
    if (value.length > 2) {
      list = list.sort((a, b) => {
        const priorityA = a.priority || 1;
        const priorityB = b.priority || 1;
        if (priorityA < priorityB) {
          return 1;
        }
        if (priorityA > priorityB) {
          return -1;
        }
        return 0;
      });
    }

    return list;
  }

  generateListItem(key, index) {
    const item = this.state.filteredData[key];
    const highLightedLabel = Helper.highlightText(
      item.label,
      this.state.value || '',
      'span'
    );
    const itemClassname = ['autocomplete__item'];

    if (this.state.selectedIndex === index) {
      itemClassname.push('autocomplete__item--selected');
    }

    return (
      <li
        className={itemClassname.join(' ')}
        key={item.id}
        onMouseDown={this.handleListClick.bind(this, item.label)}
      >
        {highLightedLabel}
      </li>
    );
  }

  // Will navigate down (and up) through the countrySelectionList.
  navigate(key) {
    const currentSelectedIndex = this.state.selectedIndex;
    const maxIndex = Object.keys(this.state.filteredData).length - 1;
    let newIndex;

    if (key === 'ArrowDown' || key === 'Tab') {
      newIndex = currentSelectedIndex < maxIndex ? currentSelectedIndex + 1 : 0;
    } else if (key === 'ArrowUp') {
      newIndex = currentSelectedIndex > 0 ? currentSelectedIndex - 1 : maxIndex;
    }

    this.setState({ selectedIndex: newIndex });

    const items =
      maxIndex >= MAX_VISIBLE_ITEMS ? MAX_VISIBLE_ITEMS : maxIndex + 1;
    this.scrollableNode.scrollTo((this.state.listHeight / items) * newIndex);

    return newIndex;
  }

  onKeyDown(e) {
    const key = e.key || e.which;
    const handleKeys = ['Tab', 'Enter', 'ArrowDown', 'ArrowUp', 'Escape'];
    const navKeys = ['Tab', 'ArrowUp', 'ArrowDown'];
    const index = this.state.selectedIndex;
    let selectedItem;

    // The user wants to 'tab' to the next field, we only allow this when there is a correct value
    // Autocomplete is closed, country exists, user clicks enter -> submit
    if (
      !key ||
      ((key === 'Tab' || key === 'Enter') &&
        this.props.data.find((country) => this.state.value === country.label))
    ) {
      return;
    }

    // Return to regular tabbing flow if the input field is empty
    if (key === 'Tab' && this.state.value === '') {
      return;
    }

    // A key is clicked that fires one of our actions -> prevent default action
    if (handleKeys.indexOf(key) > -1) {
      this.setState({ selectedIndex: 0 });
      e.preventDefault();
    }

    // A key is clicked that navigates through the list -> navigate
    if (navKeys.indexOf(key) > -1) {
      this.navigate(key);
    }

    // Escape key -> hide the list.
    if (key === 'Escape') {
      this.setState({ hideList: true });
    }

    // Return key. The value in the field doesn't exist in the list? -> do nothing, if it does exist set that value in our elements
    if (key === 'Enter') {
      selectedItem = this.state.filteredData[index];
      if (!this.state.value || !selectedItem || !selectedItem.id) {
        return;
      }

      this.setState({ value: selectedItem.label, hideList: true });
      this.props.onChange(selectedItem.label);
    }
  }

  onReset() {
    if (this.props.onChange && this.state.value !== '') {
      this.props.onChange('');
      this.setState({ value: '' });
    }
    this.autoCompleteTextfield.reset();
  }

  render() {
    const { label, name, description, data, variant } = this.props;
    let { valid } = this.props;
    const autoCompleteClass = ['autocomplete__list'];
    const listItems = Object.keys(this.state.filteredData).map((key, index) =>
      this.generateListItem(key, index)
    );
    const list = (
      <ul
        className="autocomplete"
        ref={(node) => (this.autocompleteNode = node)}
      >
        {listItems}
      </ul>
    );

    const hideEmptyStateMessage =
      !this.props.showEmptyStateMessage &&
      listItems.length === 1 &&
      this.state.filteredData[0].id === false;

    if (
      !this.state.value ||
      this.state.hideList ||
      !listItems ||
      hideEmptyStateMessage
    ) {
      autoCompleteClass.push('autocomplete__list--hidden');
    }

    if (
      valid === validationIndentifiers.VALID &&
      !data.find((country) => this.state.value === country.label)
    ) {
      valid = validationIndentifiers.NOT_VALID;
    }

    const content = (
      <React.Fragment>
        {this.props.icon && (
          <Icon
            key="searchbox_icon"
            className="searchbox__icon"
            type="Search"
            color={OSLO_GRAY}
          />
        )}
        {this.props.clearable && this.state.value && (
          <Icon
            key="searchbox_icon_remove"
            className="searchbox__remove"
            type="Remove"
            onClick={this.onReset}
            color={OSLO_GRAY}
          />
        )}
      </React.Fragment>
    );

    const rootClassName = classnames('autocomplete', this.props.className, {
      'autocomplete--with-icon': this.props.icon,
    });

    return (
      <div className={rootClassName}>
        <TextField
          content={content}
          ref={(node) => (this.autoCompleteTextfield = node)}
          name={name}
          type="text"
          onChange={this.handleChange}
          onKeyDown={(e) => this.onKeyDown(e)}
          autoComplete="nope"
          label={label}
          description={description}
          valid={valid}
          value={this.state.value || ''}
          onBlur={this.handleBlur}
          variant={variant}
          placeholder={this.props.placeholder}
        />
        <div
          className={autoCompleteClass.join(' ')}
          style={{ height: this.state.listHeight + 2 }}
        >
          <Scrollable ref={(node) => (this.scrollableNode = node)}>
            {list}
          </Scrollable>
        </div>
      </div>
    );
  }
}
